import { PageProps } from "gatsby";
import { IsoLangWrapPageElement } from "@src/backbone/provider/IsoLangContext";
import React, {
  PropsWithChildren,
  startTransition,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  PageContextIntl,
  PageContextProvider,
} from "@src/components/general/WrapPageElement/PageContext";
import { useDispatch } from "react-redux";
import { PageSlice } from "@src/store/reducers/PageSlice";
import runAfterLcp from "@src/utils/runAfterLcp";
import { datadogRum } from "@datadog/browser-rum";

const LazyTrackPageView = React.lazy(
  () =>
    import(
      /* webpackChunkName: "track-page-view-lazy" */
      "@src/components/general/WrapPageElement/TrackPageViewLazy"
    )
);

type WrapPageElementProps = PropsWithChildren<PageProps<any, PageContextIntl>>;

// interface IDataTcValue {
//   value: string;
// }

// interface IDataTcUrls {
//   _TAGCOMMANDERURLHEADER_: IDataTcValue;
//   _TAGCOMMANDERURLFOOTER_: IDataTcValue;
//   site: {
//     siteMetadata: {
//       siteUrl: string;
//     };
//   };
// }

const WrapPageElement = React.memo((props: WrapPageElementProps) => {
  const { children, pageContext, location } = props;
  const dispatch = useDispatch();
  const [postLcpTracking, setPostLcpTracking] = useState(false);

  useEffect(() => {
    startTransition(() => {
      dispatch(PageSlice.actions.setCurrentPageContextIntl(pageContext));
    });
  }, [pageContext, dispatch]);

  useEffect(() => {
    // RUM en tracking manuel (trackViewsManually, cf. initDatadogRum.ts) :
    // vue nommée par template de page plutôt que par URL.
    // Déclenché sur `location.pathname`, pas `pageTemplate` seul,
    // car deux pages successives peuvent partager le même template
    if (pageContext?.pageTemplate) {
      datadogRum.startView({
        name: pageContext.pageTemplate,
        context: {
          pageIntlId: pageContext.pageIntlId,
          lang: pageContext.lang,
          meta_title: pageContext.meta_title,
        },
      });
    }
  }, [
    location?.pathname,
    pageContext?.pageTemplate,
    pageContext?.pageIntlId,
    pageContext?.lang,
    pageContext?.meta_title,
  ]);

  useEffect(() => {
    // LCP-FIX: après idle post-LCP on monte le lazy chunk qui contient
    // `usePageLoad` — évite de parser/exécuter ce graphe avant la métrique LCP.
    const cancel = runAfterLcp(() => setPostLcpTracking(true));
    return cancel;
  }, []);

  return (
    <div>
      <IsoLangWrapPageElement pageContext={pageContext}>
        <PageContextProvider value={{ pageContext }}>
          {postLcpTracking && (
            <Suspense fallback={null}>
              <LazyTrackPageView
                pageContext={pageContext}
                location={location}
              />
            </Suspense>
          )}
          {children}
        </PageContextProvider>
      </IsoLangWrapPageElement>
    </div>
  );
});

export default WrapPageElement;
